/**
 * cosmetics.js — attach-point system for goose cosmetics (GDD 3.4).
 *
 * Five slots: head, body, beak, feet, aura. The goose model exposes an anchor
 * Object3D per slot at `goose.userData.attach`, and this module parents one
 * cosmetic object to each.
 *
 * The CATALOG lives with the meta agent (meta/index.js `cosmetics.catalog()`).
 * This module only knows how to mount whatever it is handed, plus a small set
 * of greybox builders so an equipped item renders as *something* before the
 * real art exists. Register real builders with `registerBuilder(id, fn)`.
 *
 * @module avatar/cosmetics
 */

import * as THREE from 'three';
import { save } from '../core/save.js';
import { mat } from '../assets3d/factory.js';

/** The five attach slots. */
export const SLOTS = Object.freeze(['head', 'body', 'beak', 'feet', 'aura']);

/**
 * id → builder. A builder returns a THREE.Object3D positioned in the anchor's
 * local space (origin at the anchor).
 * @type {Map<string, () => THREE.Object3D>}
 */
const builders = new Map();

/**
 * Register a cosmetic's geometry builder.
 * @param {string} id
 * @param {() => THREE.Object3D} build
 */
export function registerBuilder(id, build) {
  builders.set(id, build);
}

/** @returns {string[]} Every id with a registered builder. */
export function registeredIds() {
  return [...builders.keys()];
}

class Cosmetics {
  constructor() {
    /** @type {THREE.Group|null} */ this.goose = null;
    /** @type {Record<string, THREE.Object3D|null>} Mounted objects per slot. */
    this.mounted = { head: null, body: null, beak: null, feet: null, aura: null };
    this._elapsed = 0;
  }

  /**
   * @param {THREE.Group} goose A goose from `createGoose()`.
   */
  init(goose) {
    this.goose = goose;
    this.applyFromSave();
    return this;
  }

  /** Mount everything listed in `save.data.cosmetics.equipped`. */
  applyFromSave() {
    const equipped = save.data.cosmetics.equipped || {};
    for (const slot of SLOTS) this.equip(slot, equipped[slot] ?? null, { persist: false });
  }

  /**
   * Mount (or clear) a cosmetic in a slot.
   * @param {'head'|'body'|'beak'|'feet'|'aura'} slot
   * @param {string|null} id Null unequips.
   * @param {{ persist?: boolean }} [opts]
   * @returns {boolean} Whether the slot now holds something.
   */
  equip(slot, id, { persist = true } = {}) {
    if (!SLOTS.includes(slot) || !this.goose) return false;

    const anchor = this.goose.userData.attach?.[slot];
    if (!anchor) return false;

    if (this.mounted[slot]) {
      anchor.remove(this.mounted[slot]);
      disposeTree(this.mounted[slot]);
      this.mounted[slot] = null;
    }

    if (persist) {
      save.data.cosmetics.equipped[slot] = id;
      save.markDirty();
    }
    if (!id) return false;

    const build = builders.get(id) || fallbackBuilder(slot, id);
    const obj = build();
    if (!obj) return false;
    obj.name = `cosmetic-${slot}-${id}`;
    anchor.add(obj);
    this.mounted[slot] = obj;
    return true;
  }

  /** @returns {Record<string, string|null>} */
  equipped() {
    return { ...save.data.cosmetics.equipped };
  }

  /** @param {string} id @returns {boolean} */
  owns(id) {
    return save.data.cosmetics.owned.includes(id);
  }

  /**
   * Add a cosmetic to the player's collection.
   * @param {string} id
   * @returns {boolean} False if already owned.
   */
  unlock(id) {
    if (this.owns(id)) return false;
    save.data.cosmetics.owned.push(id);
    save.markDirty();
    return true;
  }

  /** Animate anything that needs it (auras pulse and spin). */
  update(dt) {
    this._elapsed += dt;
    const aura = this.mounted.aura;
    if (aura && aura.userData.animate) aura.userData.animate(this._elapsed, dt);
  }
}

/**
 * Greybox stand-in so an equipped-but-unbuilt cosmetic is still visible.
 * @param {string} slot
 * @param {string} id
 * @returns {() => THREE.Object3D}
 */
function fallbackBuilder(slot, id) {
  return () => {
    const g = new THREE.Group();
    if (slot === 'aura') {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.42, 0.02, 8, 24),
        new THREE.MeshBasicMaterial({ color: 0xcba6ff, transparent: true, opacity: 0.6 }),
      );
      ring.rotation.x = Math.PI / 2;
      g.add(ring);
      g.userData.animate = (t) => {
        ring.rotation.z = t * 0.9;
        ring.position.y = Math.sin(t * 1.6) * 0.06;
      };
      return g;
    }
    if (slot === 'head') {
      const hat = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.16, 10), mat(0x9b5de5, { rough: 0.7 }));
      hat.position.y = 0.09;
      hat.castShadow = true;
      g.add(hat);
      return g;
    }
    const box = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.05, 0.08), mat(0x6fe7c4, { rough: 0.6 }));
    box.castShadow = true;
    g.add(box);
    return g;
  };
}

function disposeTree(obj) {
  obj.traverse((child) => {
    if (child.geometry) child.geometry.dispose();
    // Materials come from the shared cache — disposing them would blank other
    // objects using the same colour.
  });
}

/** The one cosmetics system. */
export const cosmetics = new Cosmetics();
